import {Injectable} from '@angular/core';

/**
 * Tracks active froala editor instances that may have an open code view
 * (CodeMirror) and provides a single entry point to flush their pending
 * code view content back into the underlying froala model.
 *
 * Background: froala only syncs CodeMirror content into its model when the
 * user manually leaves the code view, when the editor is destroyed, or on
 * a real form submit. Saving the questionnaire via button click or Ctrl+S
 * is none of those, so the model would otherwise contain stale html and
 * any unsaved code edits would be lost.
 *
 * Editors register themselves via the froala "initialized" event and
 * unregister on "destroy". Tracking happens via a Set, not via the DOM or
 * froala internal registries, because the angular wrapper instantiates
 * editors from an esm copy of froala-editor whose internal state is not
 * accessible from window.FroalaEditor or jQuery.data lookups.
 */
@Injectable({providedIn: 'root'})
export class FroalaCodeViewFlushService {

  private instances = new Set<any>();

  /**
   * Registers a froala editor instance for code view flushing
   */
  public register(instance: any): void {
    if (!instance) {
      return;
    }
    this.instances.add(instance);
  }

  /**
   * Unregisters a froala editor instance, e.g. when the editor is destroyed
   */
  public unregister(instance: any): void {
    if (!instance) {
      return;
    }
    this.instances.delete(instance);
  }

  /**
   * Iterates over all registered editors and forces any open code view to
   * sync its content back into the froala model. Mirrors what froala itself
   * does on a real form submit. Returns a promise that resolves on the next
   * microtask so the angular two-way [(froalaModel)] binding has a chance
   * to propagate the new value before the caller proceeds.
   */
  public flushAll(): Promise<void> {
    for (const instance of this.instances) {
      if (!instance || !instance.codeView || !instance.codeView.isActive || !instance.codeView.isActive()) {
        continue;
      }
      instance.html.set(instance.codeView.get());
      instance.events.trigger('contentChanged', [], true);
    }
    return new Promise(resolve => setTimeout(resolve, 0));
  }
}
